import { Capacitor } from '@capacitor/core'

export const progressStorageKey = 'phonics-garden.progress.v2'
export const nativeProgressKey = 'phonics-garden.progress.native.v1'
export const nativeJournalKey = 'phonics-garden.progress.native-journal.v1'

interface StringStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}
interface PreferencesPort {
  get(options: { key: string }): Promise<{ value: string | null }>
  set(options: { key: string; value: string }): Promise<void>
}
interface Snapshot { version: 1; revision: number; progress: string }

function validateProgress(raw: string) {
  const value: unknown = JSON.parse(raw)
  if (!value || typeof value !== 'object' || Array.isArray(value) || !('version' in value) || value.version !== 2) {
    throw new Error('The stored native progress is not a supported progress record.')
  }
}

function parseSnapshot(raw: string | null): Snapshot | undefined {
  if (raw === null) return undefined
  const value: unknown = JSON.parse(raw)
  if (!value || typeof value !== 'object' || !('version' in value) || value.version !== 1
    || !('revision' in value) || !Number.isSafeInteger(value.revision) || Number(value.revision) < 0
    || !('progress' in value) || typeof value.progress !== 'string') {
    throw new Error('The stored native snapshot is invalid. It has not been overwritten.')
  }
  validateProgress(value.progress)
  return value as Snapshot
}

/** One native writer. The journal protects newer unsent work after a failed backup. */
export class NativeProgressPersistence {
  private ready = false
  private current: Snapshot | undefined
  private queue: Promise<void> = Promise.resolve()
  private failed = false

  constructor(
    private readonly storage: StringStorage,
    private readonly preferences: PreferencesPort,
    private readonly onStatus: (failed: boolean) => void = () => undefined,
  ) {}

  async hydrate() {
    // Do not render the provider until this succeeds: its initial save must not
    // replace native data after a temporary read failure or malformed snapshot.
    const native = parseSnapshot((await this.preferences.get({ key: nativeProgressKey })).value)
    let localRaw: string | null = null
    try { localRaw = this.storage.getItem(nativeJournalKey) } catch (error) {
      if (!native) throw error
    }
    const local = parseSnapshot(localRaw)
    if (local && native && local.revision === native.revision && local.progress !== native.progress) {
      throw new Error('Native progress snapshots conflict. Neither copy has been overwritten.')
    }
    this.current = local && (!native || local.revision > native.revision) ? local : native
    if (!this.current) {
      // Existing WKWebView v2 data can seed the first native backup. Safari data
      // belongs to a different origin and is deliberately not claimed as migrated.
      const existing = this.storage.getItem(progressStorageKey)
      if (existing !== null) {
        validateProgress(existing)
        this.current = { version: 1, revision: 0, progress: existing }
      } else {
        const legacy = this.storage.getItem('phonics-garden.progress.v1')
        if (legacy !== null) {
          const value: unknown = JSON.parse(legacy)
          if (!value || typeof value !== 'object' || Array.isArray(value)) {
            throw new Error('The legacy native progress is invalid. It has not been overwritten.')
          }
        }
      }
    }
    this.ready = true
    return this.current?.progress
  }

  getCurrent() { return this.current?.progress }
  hasFailed() { return this.failed }

  save(progress: string) {
    if (!this.ready) throw new Error('Native progress must be hydrated before saving.')
    validateProgress(progress)
    const snapshot: Snapshot = { version: 1, revision: Math.max(Date.now(), (this.current?.revision ?? 0) + 1), progress }
    this.current = snapshot
    const serialized = JSON.stringify(snapshot)
    // Journal first: a process termination between these writes cannot restore
    // an older Preferences snapshot over the newer local progress.
    try { this.storage.setItem(nativeJournalKey, serialized) } catch { /* Preferences remains the native persistence path. */ }
    try { this.storage.setItem(progressStorageKey, progress) } catch { /* Current in-memory state is retained until the backup resolves. */ }
    this.enqueue(snapshot)
  }

  private enqueue(snapshot: Snapshot) {
    this.queue = this.queue.then(async () => {
      try {
        await this.preferences.set({ key: nativeProgressKey, value: JSON.stringify(snapshot) })
        this.failed = false
        this.onStatus(false)
      } catch {
        // Consume this failure so subsequent saves can still run in order.
        this.failed = true
        this.onStatus(true)
      }
    })
  }

  retry() {
    if (this.current) this.enqueue(this.current)
    return this.flush()
  }

  flush() { return this.queue }
}

let nativePersistence: NativeProgressPersistence | undefined
const listeners = new Set<(failed: boolean) => void>()
export const getNativeProgressPersistence = () => nativePersistence
export function subscribeNativePersistence(listener: (failed: boolean) => void) {
  listeners.add(listener)
  listener(nativePersistence?.hasFailed() ?? false)
  return () => { listeners.delete(listener) }
}

export async function initializeNativeProgressPersistence() {
  if (!Capacitor.isNativePlatform()) return
  const { Preferences } = await import('@capacitor/preferences')
  const persistence = new NativeProgressPersistence(window.localStorage, Preferences, (failed) => {
    for (const listener of listeners) listener(failed)
  })
  await persistence.hydrate()
  nativePersistence = persistence
}
